/**
 * Order notes parser.
 *
 * Zoho order notes arrive as an unstructured prose blob that frequently contains
 * operational information the warehouse needs *before* packing — access
 * constraints, pallet restrictions, required delivery dates — and which is
 * currently invisible on every fulfilment surface.
 *
 * This pulls the structured facts out so they can be surfaced as fields rather
 * than buried in a paragraph. It is deliberately forgiving: the source text is
 * concatenated by upstream systems and is often duplicated.
 */

export interface ParsedOrderNotes {
  /** ISO date string (yyyy-mm-dd) if a required delivery date was found. */
  requiredDeliveryDate: string | null;
  /** Sentences that describe a delivery/access constraint the packer must know. */
  constraints: string[];
  /** Where the order originated, if stated (e.g. "Trade Portal"). */
  channel: string | null;
  /** Everything else, de-duplicated. */
  rest: string[];
  /** The original text, untouched. */
  raw: string;
}

/**
 * Words that mark a sentence as operationally important at packing time.
 * Kept deliberately narrow — a false positive costs the packer attention.
 */
const CONSTRAINT_PATTERNS = [
  /pallet/i,
  /tail[- ]?lift/i,
  /fork ?lift/i,
  /loading bay/i,
  /entrance|shop door|side door|rear door|access/i,
  /advance[d]? notice|call ahead|ring ahead|book(ing)? in\b/i,
  /no parking|restricted parking|permit/i,
  /delivery (hours|window|slot)/i,
  /closed (on|between)|half day/i,
  /fragile|do not stack/i,
];

const DATE_LABEL = /required\s+delivery\s+date\s*[:\-–]?\s*/i;

/** yyyy-mm-dd, dd/mm/yyyy, dd-mm-yyyy */
const DATE_VALUE =
  /(\d{4}-\d{2}-\d{2})|(\d{1,2}[/-]\d{1,2}[/-]\d{4})/;

/**
 * "Order placed by Trade Portal", "Order placed via Trade Portal by Tolsey Two".
 *
 * The source text has no punctuation between the channel phrase and the free
 * text that follows it, so the boundary is capitalisation: a run of Capitalised
 * words is the channel (and the optional trailing customer name), and the first
 * lower-case word ends it. "Order" and "Required" are excluded so a repeated
 * phrase doesn't get swallowed into the previous match.
 */
const CAP_WORD = String.raw`(?!Order|Required)[A-Z][\w&'().-]*`;
const CAP_RUN = String.raw`${CAP_WORD}(?:\s+${CAP_WORD}){0,3}`;
/**
 * Deliberately NOT case-insensitive: the `i` flag would make `[A-Z]` match
 * lower-case letters, so the capitalised run would swallow "by"/"via" and the
 * free text that follows. Only the fixed lead-in words allow either case.
 */
const CHANNEL_SOURCE = String.raw`[Oo]rder\s+[Pp]laced\s+(?:[Bb]y|[Vv]ia|[Tt]hrough)\s+(${CAP_RUN})(?:\s+[Bb]y\s+${CAP_RUN})?`;
const CHANNEL_PATTERN = new RegExp(CHANNEL_SOURCE);

function toIso(value: string): string | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const m = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Split on sentence enders and semicolons, keeping fragments intact. */
function toSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+|\s*;\s*|\n+/)
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter((s) => s.length > 1);
}

/**
 * Constraints are usually written as one comma-spliced run-on. Three short
 * bullets are far easier to act on at packing time than one long line, so
 * split on commas when the result is genuinely a list of clauses.
 */
function splitClauses(sentence: string): string[] {
  if (!sentence.includes(',')) return [sentence];
  const parts = sentence
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length < 2 || parts.some((p) => p.length < 12)) return [sentence];
  return parts;
}

/** Case-insensitive de-duplication that preserves first-seen casing and order. */
function dedupe(sentences: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of sentences) {
    const key = s.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

export function parseOrderNotes(notes: string | null | undefined): ParsedOrderNotes {
  const raw = notes ?? '';
  const empty: ParsedOrderNotes = {
    requiredDeliveryDate: null,
    constraints: [],
    channel: null,
    rest: [],
    raw,
  };
  if (!raw.trim()) return empty;

  let working = raw.replace(/\s+/g, ' ').trim();

  // 1. Required delivery date — take the first, strip every occurrence.
  let requiredDeliveryDate: string | null = null;
  const dateLabelGlobal = new RegExp(
    `${DATE_LABEL.source}(${DATE_VALUE.source})`,
    'gi',
  );
  const dateMatches = [...working.matchAll(dateLabelGlobal)];
  if (dateMatches.length > 0) {
    requiredDeliveryDate = toIso(dateMatches[0][1]);
    working = working.replace(dateLabelGlobal, ' ');
  }

  // 2. Origin channel — take the first, strip every occurrence.
  let channel: string | null = null;
  const channelMatch = working.match(CHANNEL_PATTERN);
  if (channelMatch) {
    channel = channelMatch[1].trim();
    working = working.replace(new RegExp(CHANNEL_SOURCE, 'g'), ' ');
  }

  // 3. Split what's left, de-duplicate, and partition by importance.
  const sentences = dedupe(toSentences(working));
  const constraints: string[] = [];
  const rest: string[] = [];
  for (const sentence of sentences) {
    if (CONSTRAINT_PATTERNS.some((p) => p.test(sentence))) constraints.push(...splitClauses(sentence));
    else rest.push(sentence);
  }

  return { requiredDeliveryDate, constraints, channel, rest, raw };
}

/** True when the notes contain something a packer must read before packing. */
export function hasPackingCriticalNotes(notes: string | null | undefined): boolean {
  const parsed = parseOrderNotes(notes);
  return parsed.constraints.length > 0 || parsed.requiredDeliveryDate !== null;
}
